import PageHeader from "@/components/page-header";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import {
  getAuth0EmailVerificationStatusOptions,
  resendAuth0VerificationEmailMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/account-setup/verify-email")({
  component: VerifyEmail,
});

function VerifyEmail() {
  const { user } = useAuth0();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const navigate = useNavigate();
  const [cooldown, setCooldown] = useState(0);
  const resendMutation = useMutation({
    ...resendAuth0VerificationEmailMutation(),
  });

  const { data } = useQuery({
    ...getAuth0EmailVerificationStatusOptions(),
    enabled: isAuthSetupComplete,
    refetchInterval: (query) => {
      // Stop polling if email is verified
      if (query.state.data?.is_verified) {
        return false;
      }
      return 5000; // Check every 5 seconds if not verified
    },
  });

  // Perform navigation in an effect
  useEffect(() => {
    if (data?.is_verified) {
      navigate({ to: "/registrant/dashboard" });
    }
  }, [data?.is_verified, navigate]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldown]);

  const handleResend = async () => {
    try {
      await resendMutation.mutateAsync({});
      setCooldown(300); // 5 minutes in seconds
      toast({
        title: "Success",
        description: "Verification email sent. Please check your inbox.",
        variant: "secondary",
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          "Failed to send verification email. Please try again later.",
        variant: "destructive",
      });
    }
  };

  const formatCooldown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex flex-col container mx-auto max-w-3xl">
      <PageHeader
        title="Verify Your Email"
        subtitle="Check your email inbox for a verification link"
        action={<ContextHelpButton articleId="account-setup" />}
      />
      <Card className="mt-6">
        <CardContent className="space-y-4">
          <p>
            Please check your <strong>{user?.email}</strong> inbox for a
            verification link. Click the link in the email to verify your email
            address.
          </p>
          <p>If you don't see the email, please check your spam folder.</p>
          <p>
            If you still can't find it, you can also request a new verification
            email below.
          </p>
        </CardContent>
        <CardFooter>
          <Button
            onClick={handleResend}
            disabled={cooldown > 0 || resendMutation.isPending}
          >
            {resendMutation.isPending
              ? "Sending..."
              : cooldown > 0
                ? `Resend in ${formatCooldown(cooldown)}`
                : "Resend Verification Email"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
